import { Clickable, Col, Icon, SegmentControl, Text } from 'components';
import { theme } from 'lib';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';
import {
  SendShoutout,
  SendShoutoutCheckout,
  Shoutout,
  ShoutoutCheckout,
  ShoutoutDetails,
  ShoutoutSuccess
} from './screens';

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const ModalContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: ${(props) => (props.visible ? 'flex' : 'none')};
  justify-content: center;
  align-items: center;
  animation: ${fadeIn} 0.2s ease-in-out;
  z-index: 9999;
`;

/*
  Team shout-outs are composed on the same purple hero gradient as the CTA
  share-link flow, so the modal swaps its white fill for it ($dark).
*/
const ModalContent = styled.div`
  background: ${({ $dark }) => ($dark ? theme.TEAM_HERO_GRADIENT_COMPACT : '#fff')};
  padding: 20px;
  width: 90%;
  max-width: 500px;
  border-radius: 10px;
  overflow-y: auto;
  height: min(85vh, 720px);
`;

const Tab = styled.div`
  font-size: 16px;
  font-weight: bold;
  padding: 10px 20px;
  cursor: pointer;
  border-radius: 20px;
  background-color: ${(props) => (props.active ? 'red' : 'gray')};
  color: ${(props) => (props.active ? 'white' : 'black')};
  transition:
    background-color 0.3s ease,
    color 0.3s ease;
  z-index: ${(props) => (props.active ? 1 : 0)};
  margin-right: ${(props) => (props.active ? '-10px' : '0')};
`;

const CloseContainer2 = styled.div`
  position: relative;
  height: 35px;
  display: flex;
  justify-content: flex-end;
  right: 0;
  top: 0;
`;

const ModalHeader = styled.div`
  background-color: ${theme.SECONDARY};
  margin: -20px -20px 0 -20px;
  padding: 16px 20px;
  border-radius: 10px 0 10px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const ModalHeaderClose = styled.div`
  position: absolute;
  right: 20px;
`;

const AddMobileShoutout = ({
  userData,
  visible,
  setVisible,
  preSelectData = {},
  preSelectPage = 'shoutout',
  preSelect = 0,
  // fired once a shout-out is actually created, so the surface behind the modal
  // (e.g. the team channel feed) can refresh itself
  onShoutoutPosted
}) => {
  const [data, setData] = useState(preSelectData);
  const [page, setPage] = useState(preSelectPage);
  const [selected, setSelected] = useState(preSelect);
  const { t } = useTranslation();
  const [errors, setErrors] = useState([]);

  // the compose step of a team shout-out is the only page rendered on the
  // gradient — a team never continues to the checkout screens
  const isTeamCompose =
    selected === 1 &&
    page === 'sendShoutout' &&
    (!!data.eventObject || data.entity?.type === 'team');

  useEffect(() => {
    // always set the updated props, since this component won't be removed from its parent component
    setPage(preSelectPage);
    setSelected(preSelect);
  }, [preSelectPage, preSelect]);

  const handleCloseModal = () => {
    setVisible(false);
    setData({
      ...data,
      reqName: '',
      introduction: '',
      gender: '',
      instructions: '',
      addInstruction: '',
      comment: '',
      // the picked preset message is part of the draft, so it resets with it
      presetId: null,
      // entity: null,
      media: []
    });
    setErrors([]);

    setPage(preSelectPage);
    setSelected(preSelect);
  };

  return (
    <ModalContainer visible={visible}>
      <ModalContent $dark={isTeamCompose}>
        <ModalHeader>
          <Text h3 col="#fff">
            {t('callToAction.sendAShoutout')}
          </Text>
          <ModalHeaderClose>
            <Clickable onClick={handleCloseModal}>
              <Icon type="close" dimensions={10} col="#fff" />
            </Clickable>
          </ModalHeaderClose>
        </ModalHeader>

        {/* <Col>
          <CloseContainer2>
            <Clickable onClick={handleCloseModal}>
              <Icon type="close" dimensions={10} col={theme.GREY_60} />
            </Clickable>
          </CloseContainer2>
        </Col>
        <SegmentControl
          useFlex
          options={[t('requestShoutout.requestAShoutout'), t('callToAction.sendAShoutout')]}
          selectedIdx={selected}
          handleSelect={(opt) => {
            setSelected(opt);
            if (opt === 1) {
              setPage('sendShoutout');
            } else {
              setPage('shoutout');
            }
          }}
        /> */}

        <Col
          marg="15px 0 0"
          wid="100%"
          noFlex
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center', // Centers content horizontally
            justifyContent: 'center' // Centers content vertically if necessary
          }}
        >
          {selected === 0 && page === 'shoutout' && (
            <Shoutout
              data={data}
              setData={setData}
              handleCancel={() => setVisible(false)}
              handleNext={() => setPage('shoutoutCheckout')}
              errors={errors}
              setErrors={setErrors}
            />
          )}

          {/* Debugging text (optional, you can remove it later) */}
          {selected === 0 && page === 'shoutoutCheckout' && (
            <ShoutoutCheckout
              data={data}
              preSelected={preSelectPage !== 'shoutout'}
              handleNext={() => setPage('shoutoutSuccess')}
              userData={userData}
              handleBack={() => setPage('shoutout')}
            />
          )}

          {selected === 0 && page === 'shoutoutSuccess' && (
            <ShoutoutSuccess
              handleClose={() => {
                setVisible(false);
                setPage(preSelectPage);
                setData(preSelectData);
              }}
              userData={userData}
            />
          )}

          {selected === 1 &&
            page === 'sendShoutout' &&
            !data.entity?._id &&
            !data.eventObject?._id && <SendShoutout data={data} setData={setData} />}

          {selected === 1 &&
            page === 'sendShoutout' &&
            (data.entity?._id || data.eventObject?._id) && (
              <ShoutoutDetails
                data={data}
                setData={setData}
                handleClose={handleCloseModal}
                handleNext={() => setPage('shoutoutRequestCheckout')}
                onShoutoutPosted={onShoutoutPosted}
                visible={visible}
              />
            )}

          {selected === 1 && page === 'shoutoutRequestCheckout' && (
            <SendShoutoutCheckout
              data={data}
              setData={setData}
              handleBack={() => setPage('sendShoutout')}
              handleClose={handleCloseModal}
              onShoutoutPosted={onShoutoutPosted}
            />
          )}
        </Col>
      </ModalContent>
    </ModalContainer>
  );
};

export default AddMobileShoutout;
